<template>
    <transition name="fade">
      <div v-if="visible" class="fixed" @click.stop>
        <div class="bg-white popup">
          <div class="popup-header">
            <h1 class="title">{{ props.purchaseData.purchase.codigo }}</h1>
            <img src="@/assets/images/ic_close.svg" class="close_icon" @click="closePopup()" />
          </div>
          
          <div class="popup-body">
            <div class="expiration-date-container">
              <label style="margin-right: 20px;">Fecha de validez:</label>
              <input
                type="date"
                required
                v-model="expirationDate" 
                class="expiration-date-input"/>
            </div>
            <div class="user-info">
                <img :src="purchaseData.user.foto" alt="Imagen del usuario" class="circular-image" />
                <div class="user-text">
                  <h2 style="margin-bottom: 0px; margin-top: 0px;">{{ purchaseData.user.nombre }}</h2>
                  <label>{{ purchaseData.user.email }}</label>
                  <label>Total planes comprados: {{ purchaseData.user.promos_compradas }}</label>
                </div>
              </div>
              <div class="questions" v-if="containsQuestions">
                <h3 style="margin-bottom: 0px; margin-top: 0px;">Información solicitada</h3>
                <label v-for="item in purchaseData.purchase.campos" :key="item.orden">
                  <b>{{ item.pregunta }}:</b> {{ item.respuesta }}<br/>
                </label>
              </div>
              <div class="products">
                <h3 style="margin-bottom: 0px; margin-top: 0px;">Productos comprados</h3>
                <PurchasedProductDetails 
                  v-for="product in purchaseData.purchase.productos"
                  :key="product.id_usuario_producto"
                  :purchaseData="purchaseData"
                  :product="product"
                  @userProductsChanged="userProductsChanged()"
                  ref="productDetailsReferences"
                  />
              </div>
          </div>
        </div>
      </div>
    </transition>
    <ErrorPopup :errorMessage="error" />
  </template>
  
  <script setup>
  import { ref, watch, defineProps, defineEmits } from 'vue';
  import ErrorPopup from "@/components/common/ErrorPopup.vue";
  import PurchasedProductDetails from './PurchasedProductDetails.vue';
  import { useLoadingStore } from "@/stores/loadingStore";
  import { refreshToken } from "@/services/authService";
  import { useRouter } from 'vue-router';

  const props = defineProps({
    purchaseData: Object,
  });
  
  const emit = defineEmits(['userProductsChanged']);

  const visible = ref(false);
  const containsQuestions = ref(false);
  const products = ref(null);
  const productDetailsReferences = ref([]);
  const expirationDate = ref(null);
  const loadingStore = useLoadingStore();
  const router = useRouter();
  const error = ref(null);

  watch(() => props.purchaseData, (newVal) => {
    visible.value = !!newVal;
    containsQuestions.value = newVal?.purchase?.campos.length > 0;
    products.value = newVal?.purchase?.productos;
    expirationDate.value = newVal?.purchase?.fecha_validez;
  });
  
  watch(expirationDate, (newVal) => {
    if (!props.purchaseData || !props.purchaseData.purchase) return;
  
    if (newVal != props.purchaseData.purchase.fecha_validez) {
      updateExpirationDate(newVal);
    }
  });

  const closePopup = () => {
    visible.value = false;
    history.back();
  };

  const userProductsChanged = () => {
    emit('userProductsChanged', props.purchaseData.purchase.codigo);
    visible.value = false;
    history.back();
  };

  const updateExpirationDate = async (newExpirationDate) => {
    loadingStore.isLoading = true;
    error.value = null;

    try {
      const date = new Date(newExpirationDate);
      const formatted = date.toISOString().split("T")[0];

      let response = await updateExpirationDateInAPI(formatted);

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await updateExpirationDateInAPI(formatted);
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        error.value = "Se ha producido un error cambiando la fecha de validez.";
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
      } else {
        emit('userProductsChanged', props.purchaseData.purchase.codigo);
        visible.value = false;
        history.back();
      }
    } catch (exception) {
      error.value = exception.message;
    } finally {
      loadingStore.setLoading(false);
    }
  };

  async function updateExpirationDateInAPI(newExpirationDate) {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/reservations/edit_expiration_date", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "promo_user_id": props.purchaseData.user.id,
        "purchase_code": props.purchaseData.purchase.codigo,
        "new_expiration_date": newExpirationDate
      }),
    });
    
  }

  </script>
  
  <style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  
  .fixed {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
  
  .bg-white {
    background-color: white;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .popup-header {
    padding: var(--spacing-md) var(--spacing-lg);
    border-bottom: 1px solid var(--light-gray);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .title {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-color-dark);
    margin: 0;
  }

  .close_icon {
    position: absolute;
    right: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity var(--transition-fast);
  }

  .close_icon:hover {
    opacity: 1;
  }

  .popup-body {
    padding: var(--spacing-lg);
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .circular-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 20px;
    border: 2px solid var(--light-gray);
  }

  .user-info {
    display: flex;
    align-items: center;
    padding-bottom: var(--spacing-md);
  }

  .user-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .user-text h2 {
    font-size: 18px;
    color: var(--text-color-dark);
  }
  
  .user-text label {
    font-size: 14px;
    color: var(--text-color-light);
  }

  .questions {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--light-gray);
    padding-top: 15px;
  }

  .questions h3 {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
  }

  .questions label {
    margin-top: 5px;
    font-size: 14px;
  }

  .products {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--light-gray);
    padding-top: 15px;
  }

  .products h3 {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
  }

  .expiration-date-input {
    width: 200px;
    box-sizing: border-box;
    padding: 8px;
    border: 1px solid var(--gray);
    border-radius: var(--radius-md);
    font-family: inherit;
    font-size: 14px;
    transition: border-color var(--transition-fast);
  }

  .expiration-date-input:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  .expiration-date-container {
    border-bottom: 1px solid var(--light-gray);
    padding-bottom: 20px;
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
  
  .expiration-date-container label {
    font-weight: 600;
    color: var(--text-color-dark);
  }

  </style>
  